import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { Group, FlatGroup, Template, DifferenceList, Difference, ChangesToApplyDTO, ComponentDefinition } from '../../../models';
import { ButtonDefinition } from '../../../components';
import { ITemplateService, ILoggerService, IStoreService, IGroupTreeHelperService } from '../../../services';
import { StoreElement, Store } from '../../../stateManagement';
import { templateForceRefresh, guidEmpty } from '../../../common';
import { ObjectDifference } from '../../../models/ObjectDifference';

export interface SyncStateShared {
    selectedDiff?: ChangesToApplyDTO;
    openScript?: { [guid: string]: boolean };
    diff?: {
        applyAllFromTemplate?: () => void;
        applyAllFromGroup?: () => void;
        ignoreAll?: () => void;
        applyFromTemplate: (item) => void;
        applyFromGroup: (item) => void;
        ignore: (item) => void;
    };
    central?: {
        leftAction?: () => void;
        leftSelected?: () => any;
        ignoreAction?: () => void;
        ignoreSelected?: () => any;
        rightAction?: () => void;
        rightSelected?: () => any;
        isTreated: () => boolean;
        changeGuid: string;
    }[];
}

interface SyncRow {
    group: Group;
    changesCount: number;
    ignoredCount: number;
    changesByType: { key: string; values: any }[];
    template: Template;
}

const resetBadges = {
    overview: { total: 0, display: 0 },
    trend: { total: 0, display: 0 },
    properties: { total: 0, display: 0 },
    identifiers: { total: 0, display: 0 },
    states: { total: 0, display: 0 },
    rules: { total: 0, display: 0 }
};

class SynchronizationStateController {
    allGroups: Group[];
    allIgnored: { [guid: string]: Difference[] };
    allSelectedDiff: { [type: number]: ChangesToApplyDTO } = {};
    badges = resetBadges;
    buttons: ButtonDefinition[];
    button: ButtonDefinition;
    countIgnored: number;
    changes: DifferenceList;
    currentGroup: Group;
    currentGroups: Group[];
    currentTemplates: FlatGroup[];
    differences: Difference[] = [];
    flatTemplatesIndex: { [guid: string]: FlatGroup[] } = {};
    isGroupTreeHidden: boolean;
    isLoading: boolean;
    isReadOnly: boolean;
    isSynchronizing: boolean;
    share: SyncStateShared = {}; // for share between controllers
    templates: Template[];
    tuples: SyncRow[];
    selectedChangeSet: any;
    selectedGroupGuid: string;

    private _unsubscribe: () => void;

    static $inject = [
        '$q',
        '$scope',
        '$stateParams',
        'storeService',
        'groupTreeHelperService',
        'templateService',
        'loggerService',
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _storeService: IStoreService,
        private _groupTreeHelperService: IGroupTreeHelperService,
        private _templateService: ITemplateService,
        private _loggerService: ILoggerService
    ) {
    }


    $onInit() {
        this.allGroups = [];
        this.changes = {};
        this.isLoading = true;
        this.isGroupTreeHidden = false;
        this.isReadOnly = true;
        this.selectedGroupGuid = this._$stateParams['groupGuid'];

        this._$scope.$watch(() => this.selectedChangeSet, () => {
            if (this.selectedChangeSet) {
                this.reselect();
                this.refresh();
            }
        });

        this._$scope.$watch(() => this.button, () => this.reselect());

        const firstLoad = this._$q.defer<void>();
        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store, firstLoad),
            [StoreElement.Groups]
        );

        firstLoad.promise
            .then(() => this.load())
            .then(() => {
                this.reselect();
                this.refresh();
            });

        this._$scope.$on(templateForceRefresh, () => {
            this.resetPage()
                .then(() => this.load())
                .then(() => {
                    this.reselect();
                    this.refresh();
                }).catch(reason => {
                    console.log('Error on refresh');
                    console.log(reason);
                });
        });
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    saveDiff(confirmed: boolean) {
        this.isLoading = true;

        const postChanges = [];
        // for each object type
        Object.keys(this.allSelectedDiff).forEach(type => {
            const diffs: ChangesToApplyDTO = this.allSelectedDiff[type];

            // find all object which diff is applying
            const objectGuidList = _.uniq(
                _.map(diffs.ignores, d => (d as any).objectGuid)
                    .concat(_.map(diffs.fromGroup, d => (d as any).objectGuid))
                    .concat(_.map(diffs.fromTemplate, d => (d as any).objectGuid))
                    .concat(_.map(diffs.removeIgnores, d => (d as any).objectGuid))
            );

            // rebuild a DTO and add it to the global list
            objectGuidList.forEach(objectGuid => {
                postChanges.push({
                    type: type,
                    objectGuid: objectGuid,
                    ignores: _.filter(diffs.ignores, d => (d as any).objectGuid === objectGuid),
                    fromGroup: _.filter(diffs.fromGroup, d => (d as any).objectGuid === objectGuid),
                    fromTemplate: _.filter(diffs.fromTemplate, d => (d as any).objectGuid === objectGuid),
                    removeIgnores: _.filter(diffs.removeIgnores, d => (d as any).objectGuid === objectGuid)
                } as any);
            });
        });

        if (!confirmed && _.some(postChanges, p =>
            _.some(p.fromGroup, g => (g as any).missGroup) ||
            _.some(p.fromTemplate, t => (t as any).missTemplate))) {
            angular.element('#deleteIdentifierModal').modal('show');
        } else {
            this._templateService.applyChanges(postChanges)
                .then(() => this.resetPage())
                .then(() => {
                    this.load();
                    this._loggerService.logSuccess('Changes applied');
                }).catch(reason => {
                    this._loggerService.logError('Cannot apply changes', true, reason);
                }).finally(() => {
                    this.isLoading = false;
                });
        }
    }

    selectButton(button) {
        if (!this.allSelectedDiff[button.objectType] && this.selectedChangeSet && this.selectedChangeSet.group) {
            const ignored = {};
            if (this.allIgnored && this.allIgnored[this.selectedChangeSet.group.guid]) {
                _.forEach(this.allIgnored[this.selectedChangeSet.group.guid], (d: any) => {
                    if (d.objectType === button.objectType) {
                        ignored[d.guid] = d;
                    }
                });
            }

            this.allSelectedDiff[button.objectType] = {
                objectGuid: this.selectedChangeSet.group.guid,
                type: button.objectType,
                ignores: ignored,
                count: 0,
                fromGroup: {},
                fromTemplate: {},
                removeIgnores: {}
            };

            (this.allSelectedDiff[button.objectType] as any).count = Object.keys(ignored).length;
        }
        this.share.selectedDiff = this.allSelectedDiff[button.objectType];
        this.button = button;
    }

    toggleGroupsTree() {
        this.isGroupTreeHidden = !this.isGroupTreeHidden;
    }

    missingInTemplate(d) {
        return !d.originalValue;
    }

    missingInGroup(d) {
        return !d.copyValue;
    }

    applyAllFromTemplate() {
        this.share.diff.applyAllFromTemplate();
    }

    applyAllFromGroup() {
        this.share.diff.applyAllFromGroup();
    }

    ignoreAll() {
        this.share.diff.ignoreAll();
    }

    applyAllForAllFromTemplate() {
        this.isLoading = true;
        this.isSynchronizing = true;
        this._templateService
            .applyAllForGroup(this.currentGroup.guid)
            .then(() => {
                this._storeService.dispatchers().groups
                    .getAll(true)
                    .then(() => this.load())
                    .then(() => {
                        this.reselect();
                        this.refresh();
                    });
            })
            .finally(() => {
                this.isLoading = false;
                this.isSynchronizing = false;
            });
    }

    private refreshStore(store: Store, defer: angular.IDeferred<void>) {
        this.allGroups = _.values(store.groups.dictionary);
        defer.resolve();
    }

    private resetPage(): angular.IPromise<Group[]> {
        this.allSelectedDiff = {};

        return this._$q.all([
                this._storeService.dispatchers().valueViewModels.getAll(true),
                this._storeService.dispatchers().groups.getAll(true),
                this._storeService.dispatchers().alarms.getAll(true)
            ])
            .then(results => {
                return results[1];
            });
    }

    private reselect() {
        if (this.button && this.selectedChangeSet) {
            const byType: { key: string; values }[] = this.selectedChangeSet.changesByType;
            const v = _.find(byType, d => parseInt(d.key) === this.button.objectType);
            this.differences = v ? v.values : [];
        }
    }

    private load(): angular.IPromise<SyncRow[]> {
        this.isLoading = true;
        this.allIgnored = {};

        return this._templateService
            .getAll()
            .then(data => {
                this.templates = data;
                this.templates.forEach(t => {
                    this.flatTemplatesIndex[t.guid] = this.buildTemplateHierarchy(t);
                });
                return this._templateService.getAllChanges();
            })
            .then(data => {
                this.changes = data;
                const nest = d3.nest<ObjectDifference>().key(d => d.type.toString());
                const tuples = this.allGroups
                    .filter(d => d.templateGuid && d.templateGuid !== guidEmpty)
                    .map(g => {
                        const changes = data[g.guid];

                        let byType: { key: string; values: any; }[] = [];
                        if (changes) {

                            byType = nest.entries(changes);
                            byType.forEach(objectChangeList => {
                                objectChangeList.values.forEach(objectChange => {
                                    const diffs = objectChange.differenceCollection;
                                    if (diffs) {
                                        _.forEach(diffs, (d: any) => {
                                            d.objectType = objectChange.type;
                                            d.objectGuid = objectChange.objectGuid;
                                        });
                                    }
                                });
                            });
                        }
                        const differences = _(changes).map('differenceCollection').flatten().value() as Difference[];
                        this.allIgnored[g.guid] = differences.filter(d => d.isIgnored);
                        return {
                            group: g,
                            changesCount: differences.filter(c => !c.isIgnored).length,
                            ignoredCount: differences.filter(c => c.isIgnored).length,
                            changesByType: byType,
                            template: _.find(this.templates, t => t.guid === g.templateGuid)
                        };
                    });
                this.tuples = tuples.sort((a, b) => d3.ascending(a.group.uniqueName, b.group.uniqueName));
                return tuples;
            }).finally(() => {
                this.isLoading = false;

                if (this.selectedChangeSet) {
                    // reselect same group if possible
                    this.selectedChangeSet = _.find(this.tuples, (t: any) =>
                        t.group.absoluteName === this.selectedChangeSet.group.absoluteName);
                } else {
                    // in other case select the selected group when arriving on page
                    this.selectedChangeSet = _.find(this.tuples, t => t.group.guid === this.selectedGroupGuid);
                }

                // reselect same button or else the first one
                this.selectButton(this.button ? this.button : this.buttons[0]);
            });
    }

    private refresh() {
        this.syncBadges();
        this.currentGroup = this.selectedChangeSet.group;
        this.currentGroups = this.buildGroupHierarchy();
        this.currentTemplates = this.flatTemplatesIndex[this.currentGroup.templateGuid];
        this.initShare();
        // reselect same button or else the first one
        this.selectButton(this.button ? this.button : this.buttons[0]);
    }

    private syncBadges() {
        this.countIgnored = 0;
        const byType: { key: string; values }[] = this.selectedChangeSet.changesByType;
        if (this.buttons) {
            this.buttons.forEach(b => {
                const v = _.find(byType, d => parseInt(d.key) === b.objectType);
                this.badges[b.property] = v
                    ? this.getCountDifferences(v)
                    : { total: 0, display: 0 };
                this.countIgnored += (this.badges[b.property].total - this.badges[b.property].display);
            });
        }
    }

    private getCountDifferences(v: { key: string; values: any }): { total: number, display: number } {
        const values = _(v.values).map('differenceCollection').flatten().value() as Difference[];
        return {
            total: values.length,
            display: values.filter(val => !val.isIgnored).length
        };
    }

    private buildGroupHierarchy(): Group[] {
        const ret = this._groupTreeHelperService.getFlatChildren(this.allGroups, this.currentGroup.guid);
        (this.currentGroup as any).depth = 0;
        ret.unshift(this.currentGroup);
        return ret;
    }

    private buildTemplateHierarchy(
        template: Template,
        depth: number = 0,
        parentGuid?: string
    ): FlatGroup[] {
        const clone = angular.copy(template);
        let ret = [];

        clone.children = undefined;
        (clone as any).depth = depth;
        clone.parentGUID = (depth === 0) ? guidEmpty : parentGuid;
        ret.push(clone);

        depth++;
        template.children.forEach(c => {
            ret = ret.concat(this.buildTemplateHierarchy(c, depth, clone.guid));
        });

        return ret;
    }

    private initShare() {
        if (this.share.selectedDiff) {
            this.share.selectedDiff.count = 0;
            this.share.selectedDiff.fromTemplate = {};
            this.share.selectedDiff.fromGroup = {};
            this.share.selectedDiff.removeIgnores = {};
            this.share.selectedDiff.ignores = this.share.selectedDiff.ignores &&
                _.some(this.share.selectedDiff.ignores, (d: any) => d.isIgnored)
                ? this.share.selectedDiff.ignores
                : {};
        }
        if (!this.share.diff) {
            this.share.diff = {
                ignore: (item) => {
                    this.remove(this.share.selectedDiff.fromTemplate, item);
                    this.remove(this.share.selectedDiff.fromGroup, item);
                    this.addOrRemove(this.share.selectedDiff.ignores, item);
                    if (item.isIgnored) {
                        this.addOrRemove(this.share.selectedDiff.removeIgnores, item);
                    }
                    this.updateCount(this.share.selectedDiff);
                },
                applyFromGroup: (item) => {
                    this.remove(this.share.selectedDiff.ignores, item);
                    this.remove(this.share.selectedDiff.fromTemplate, item);
                    this.addOrRemove(this.share.selectedDiff.fromGroup, item);
                    this.updateCount(this.share.selectedDiff);
                },
                applyFromTemplate: (item) => {
                    this.remove(this.share.selectedDiff.ignores, item);
                    this.remove(this.share.selectedDiff.fromGroup, item);
                    this.addOrRemove(this.share.selectedDiff.fromTemplate, item);
                    this.updateCount(this.share.selectedDiff);
                }
            };
        }
    }

    private updateCount(collections) {
        this.share.selectedDiff.count =
            Object.keys(collections.fromGroup).length +
            Object.keys(collections.fromTemplate).length +
            Object.keys(collections.removeIgnores).length +
            Object.keys(collections.ignores).length;
    }

    private remove(collection, item) {
        if (collection[item.guid]) {
            delete collection[item.guid];
        }
    }

    private addOrRemove(collection, item) {
        if (collection[item.guid]) {
            delete collection[item.guid];
        } else {
            collection[item.guid] = item;
        }
    }
}

export const wtConfirmApplyAllForGroupModal: ComponentDefinition = {
    componentId: 'wtSynchronizationState',
    controller: SynchronizationStateController,
    controllerAs: 'vm',
    template: require('./synchronizationState.component.html'),
    bindings: {
    }
};